/**
 * Cart Provider
 *
 * Manages shopping cart state with localStorage persistence.
 * Blueprint Task: 2.6.2
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

import { type CartItem, type Cart } from '../services/checkoutService';

const CART_STORAGE_KEY = 'geauxfile_cart';

interface CartContextValue {
  cart: Cart;
  addItem: (item: CartItem) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  isEmpty: boolean;
  mergeCart: (serverCart: Cart) => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

function loadCartFromStorage(): Cart {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        items: Array.isArray(parsed.items) ? parsed.items : [],
        customerId: parsed.customerId,
        metadata: parsed.metadata,
      };
    }
  } catch (error) {
    console.error('Failed to load cart from storage:', error);
  }
  return { items: [] };
}

function saveCartToStorage(cart: Cart): void {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Failed to save cart to storage:', error);
  }
}

interface CartProviderProps {
  children: ReactNode;
  customerId?: string;
}

/**
 *
 */
export function CartProvider({ children, customerId }: CartProviderProps) {
  const [cart, setCart] = useState<Cart>(() => {
    const loaded = loadCartFromStorage();
    return {
      ...loaded,
      customerId: customerId ?? loaded.customerId,
    };
  });

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    saveCartToStorage(cart);
  }, [cart]);

  // Update customerId when user logs in
  useEffect(() => {
    if (customerId && cart.customerId !== customerId) {
      setCart((prev) => ({ ...prev, customerId }));
    }
  }, [customerId, cart.customerId]);

  const addItem = useCallback((item: CartItem) => {
    setCart((prev) => {
      const existingIndex = prev.items.findIndex((i) => i.id === item.id);

      if (existingIndex >= 0) {
        // Update quantity of existing item
        const newItems = [...prev.items];
        const existing = newItems[existingIndex];
        if (existing) {
          newItems[existingIndex] = {
            ...existing,
            quantity: existing.quantity + item.quantity,
          };
        }
        return { ...prev, items: newItems };
      }

      // Add new item
      return { ...prev, items: [...prev.items, item] };
    });
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setCart((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== itemId),
    }));
  }, []);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }

    setCart((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      ),
    }));
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setCart((prev) => ({ ...prev, items: [] }));
    localStorage.removeItem(CART_STORAGE_KEY);
  }, []);

  /**
   * Merge guest cart with server cart on login
   * Server items take precedence for matching IDs
   */
  const mergeCart = useCallback((serverCart: Cart) => {
    setCart((prev) => {
      const mergedItems = [...serverCart.items];

      // Add local items that aren't on server
      for (const localItem of prev.items) {
        if (!mergedItems.some((i) => i.id === localItem.id)) {
          mergedItems.push(localItem);
        }
      }

      return {
        items: mergedItems,
        customerId: serverCart.customerId ?? prev.customerId,
        metadata: { ...prev.metadata, ...serverCart.metadata },
      };
    });
  }, []);

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);
  const isEmpty = cart.items.length === 0;

  const value: CartContextValue = {
    cart,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    itemCount,
    isEmpty,
    mergeCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 *
 */
export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
